/**
 * OCPP capability gating helpers.
 *
 * Per the backend doc:
 *   "None of this applies to Emporia chargers; everything is gated by
 *    `charger.ocpp_version` being '1.6' or '2.0.1'."
 *
 * The frontend's existing `ChargerAttributes` doesn't yet expose
 * `ocpp_version`, so we read it defensively (the field will be present in
 * future backend payloads). Manufacturer-name fallback ensures we hide the
 * tabbed interface from Emporia chargers today even before backend wires
 * `ocpp_version` into `getMyChargers`.
 */

import type { ChargerAttributes } from '../../models/charger-model';
import type { OcppVersion } from '../models';

interface OcppFields {
  ocpp_version?: OcppVersion | string | null;
  ocppVersion?: OcppVersion | string | null;
  protocol?: string | null;
}

const KNOWN_OCPP_VERSIONS: ReadonlyArray<OcppVersion> = ['1.6', '2.0.1'];

const NON_OCPP_MANUFACTURERS = new Set(
  ['emporia', 'emporiaev'].map((s) => s.toLowerCase())
);

/**
 * Returns the recorded OCPP version for the charger, normalised to
 * `'1.6' | '2.0.1'` when possible, else `null`.
 */
export const getChargerOcppVersion = (
  charger: ChargerAttributes | (OcppFields & Partial<ChargerAttributes>) | null | undefined
): OcppVersion | null => {
  if (!charger) return null;
  const raw =
    (charger as OcppFields).ocpp_version ??
    (charger as OcppFields).ocppVersion ??
    (charger as OcppFields).protocol ??
    null;
  if (!raw) return null;
  const trimmed = String(raw).trim();
  if (KNOWN_OCPP_VERSIONS.includes(trimmed as OcppVersion)) {
    return trimmed as OcppVersion;
  }
  if (trimmed.startsWith('ocpp1.6') || trimmed === 'ocpp16' || trimmed === 'ocpp-1.6') return '1.6';
  if (trimmed.startsWith('ocpp2.0.1') || trimmed === 'ocpp201') return '2.0.1';
  return null;
};

/**
 * True when the charger supports any of the OCPP host-facing endpoints.
 * Falls back to manufacturer name to keep Emporia chargers gated even
 * when the backend hasn't echoed a version yet.
 */
export const isOcppCharger = (
  charger: ChargerAttributes | null | undefined
): boolean => {
  if (!charger) return false;
  const version = getChargerOcppVersion(charger);
  if (version) return true;
  const mfr = (charger.manufacturer_name || '').toLowerCase();
  if (mfr && NON_OCPP_MANUFACTURERS.has(mfr)) return false;
  // If we don't know the manufacturer, allow the page to load and let the
  // server's 404/403 surface a useful message instead of silently hiding.
  return !mfr ? true : false;
};

/**
 * Whether the OCPP 2.0.1-only features should be visible (Reservations
 * with reservation_id semantics, the dedicated diagnostics-request lifecycle,
 * etc.). 1.6 chargers use the legacy GetDiagnostics path.
 */
export const supportsOcpp201 = (
  charger: ChargerAttributes | null | undefined
): boolean => getChargerOcppVersion(charger) === '2.0.1';
